/**
 * Retry logic with exponential backoff
 * Provides resilient error handling for network requests
 */

/**
 * Default retry configuration
 */
const DEFAULT_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

/**
 * Checks if an error is retryable
 * @param {Error} error - Error to check
 * @param {object} config - Retry configuration
 * @returns {boolean} True if error is retryable
 */
export const isRetryable = (error, config = DEFAULT_CONFIG) => {
  // Network errors (no response)
  if (!error.response && error.name === 'TypeError') {
    return true;
  }

  // HTTP errors with retryable status codes
  if (error.status && config.retryableStatusCodes.includes(error.status)) {
    return true;
  }

  // Timeout errors
  if (error.name === 'AbortError' || error.message?.includes('timeout')) {
    return true;
  }

  return false;
};

/**
 * Calculates delay for next retry attempt with exponential backoff
 * @param {number} attemptNumber - Current attempt number (0-indexed)
 * @param {object} config - Retry configuration
 * @returns {number} Delay in milliseconds
 */
const calculateDelay = (attemptNumber, config = DEFAULT_CONFIG) => {
  const delay = config.initialDelay * Math.pow(config.backoffMultiplier, attemptNumber);

  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 0.3 * delay;

  return Math.min(delay + jitter, config.maxDelay);
};

/**
 * Waits for a specified duration
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Promise that resolves after the delay
 */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Executes a function with retry logic and exponential backoff
 * @param {Function} fn - Async function to execute
 * @param {object} config - Retry configuration
 * @returns {Promise} Promise that resolves with the function result
 */
export const withRetry = async (fn, config = {}) => {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  let lastError;

  for (let attempt = 0; attempt <= mergedConfig.maxRetries; attempt++) {
    try {
      return await fn(attempt);
    } catch (error) {
      lastError = error;

      // If this is the last attempt, throw the error
      if (attempt === mergedConfig.maxRetries) {
        throw error;
      }

      // Check if error is retryable
      if (!isRetryable(error, mergedConfig)) {
        throw error;
      }

      // Calculate delay and wait before retry
      const delay = calculateDelay(attempt, mergedConfig);
      console.warn(
        `Request failed (attempt ${attempt + 1}/${mergedConfig.maxRetries + 1}). Retrying in ${Math.round(delay)}ms...`,
        error
      );

      await wait(delay);
    }
  }

  // This should never be reached, but just in case
  throw lastError;
};

/**
 * Wrapper for fetch requests with retry logic
 * @param {string|Request} input - URL or Request object
 * @param {object} init - Fetch options
 * @param {object} retryConfig - Retry configuration
 * @returns {Promise<Response>} Fetch response
 */
export const fetchWithRetry = async (input, init = {}, retryConfig = {}) => {
  return withRetry(async () => {
    const response = await fetch(input, init);

    // If response is not OK and is retryable, throw an error
    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
      error.status = response.status;
      error.response = response;
      throw error;
    }

    return response;
  }, retryConfig);
};

/**
 * Creates a retry wrapper for a specific function with pre-configured settings
 * @param {Function} fn - Function to wrap
 * @param {object} config - Retry configuration
 * @returns {Function} Wrapped function with retry logic
 */
export const createRetryWrapper = (fn, config = {}) => {
  return async (...args) => {
    return withRetry(() => fn(...args), config);
  };
};

/**
 * Checks if we should retry based on response status
 * @param {Response} response - Fetch response
 * @param {object} config - Retry configuration
 * @returns {boolean} True if should retry
 */
export const shouldRetryResponse = (response, config = DEFAULT_CONFIG) => {
  if (!response) {
    return false;
  }

  return config.retryableStatusCodes.includes(response.status);
};

/**
 * Advanced retry with circuit breaker pattern
 * Prevents overwhelming a failing service
 */
export class CircuitBreaker {
  constructor(config = {}) {
    this.failureThreshold = config.failureThreshold || 5;
    this.resetTimeout = config.resetTimeout || 60000; // 1 minute
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.nextAttempt = Date.now();
  }

  async execute(fn) {
    if (this.state === 'OPEN') {
      if (Date.now() < this.nextAttempt) {
        throw new Error('Circuit breaker is OPEN');
      }
      this.state = 'HALF_OPEN';
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  onSuccess() {
    this.failureCount = 0;
    this.state = 'CLOSED';
  }

  onFailure() {
    this.failureCount++;

    if (this.failureCount >= this.failureThreshold) {
      this.state = 'OPEN';
      this.nextAttempt = Date.now() + this.resetTimeout;
    }
  }

  getState() {
    return this.state;
  }

  reset() {
    this.state = 'CLOSED';
    this.failureCount = 0;
  }
}
